/**
 * The single Havainnot load path (story #1248): reads the URL scope from useInventoryFilter()
 * and issues one `GET /findings`, optionally narrowed to a judgment-lens perspective
 * (category/source_type). Left-nav Havainnot (no perspective) and the three lenses (Datan
 * laatu / Talotekniikan toimivuus / Sisäilmaolosuhteet) all call this instead of each
 * resolving their own scope into a building_ids fan-out (docs/spec/concepts.md §2.2/§2.4).
 */
import { useEffect, useRef, useState } from "react";
import {
  listFindingsForScope,
  type Finding,
  type FindingCategory,
} from "./building";
import { useBuildingChangeTick } from "@/lib/building-changes";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { encodeInventoryScope } from "@/lib/inventory-scope-codec";

export interface FindingsPerspective {
  category?: FindingCategory;
  sourceType?: Finding["source_type"];
}

export interface ScopedFindingsResult {
  /** Null while the current scope's request is in flight. */
  findings: Finding[] | null;
  loading: boolean;
  /** Wire directly to FindingsList's `onAcknowledged` — patches the row in place so the
   *  operator sees the status change immediately, without waiting for a refetch. */
  onAcknowledged: (updated: Finding) => void;
}

export function useScopedFindings(
  perspective: FindingsPerspective = {},
): ScopedFindingsResult {
  const { filter, groupId, buildingId } = useInventoryFilter();
  const [findings, setFindings] = useState<Finding[] | null>(null);
  const { category, sourceType } = perspective;

  const scope = encodeInventoryScope({ filter, groupId, buildingId });
  const scopeKey = `${scope.building ?? ""}|${scope.group ?? ""}|${scope.city ?? ""}|${scope.type ?? ""}|${scope.status ?? ""}`;
  // A finding opening or resolving in the building in scope reloads the list in place.
  const changeTick = useBuildingChangeTick(buildingId, "findings");
  const loadedKey = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const requestKey = `${scopeKey}|${category ?? ""}|${sourceType ?? ""}`;
    // A new scope or perspective shows the loading state; a change in the same one keeps the
    // current rows on screen until the reload answers.
    if (loadedKey.current !== requestKey) setFindings(null);
    loadedKey.current = requestKey;
    listFindingsForScope(scope, { category, sourceType })
      .then((result) => {
        if (!cancelled) setFindings(result);
      })
      .catch(() => {
        if (!cancelled) setFindings([]);
      });
    return () => {
      cancelled = true;
    };
    // scopeKey captures the resolved URL scope as a stable primitive; `scope` is a fresh object
    // every render (encodeInventoryScope), so it's read for its current value here rather than
    // used as a dependency itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, category, sourceType, changeTick]);

  return {
    findings,
    loading: findings === null,
    onAcknowledged: (updated) =>
      setFindings((prev) =>
        prev ? prev.map((f) => (f.id === updated.id ? updated : f)) : prev,
      ),
  };
}
